import SectionHead from '../components/SectionHead';
import Reveal from '../components/Reveal';
import usePageMeta from '../hooks/usePageMeta';

const H2 = 'font-display text-2xl font-bold uppercase tracking-tight text-white';
const LABEL = 'font-mono text-[10px] uppercase tracking-[0.25em] text-accentText';
const BODY = 'text-sm leading-relaxed text-dim';

const SECTIONS = [
  {
    title: '1. Website Purpose',
    paragraphs: [
      'This website provides information about RangeSafe products and commercial programs for commercial business owners and managers, as well as government and institutional buyers, and other business customers. It also allows prospective customers to request information, consultations, quotations and proposals.',
      'Website information is general in nature. Product suitability, availability and commercial arrangements must be assessed for the particular appliance, property and project.',
    ],
  },
  {
    title: '2. Permitted Use',
    paragraphs: [
      'You may use this website for lawful purposes, including evaluating RangeSafe products and making legitimate commercial inquiries.',
      'You must not:',
    ],
    bullets: [
      'Submit false, misleading or fraudulent information.',
      'Impersonate another person or misrepresent your authority to act for an organization.',
      'Attempt to gain unauthorized access to the website or its systems.',
      'Introduce malicious software or interfere with website operation.',
      'Use the website in a way that infringes another person’s rights or violates applicable law.',
    ],
    after: [
      'When acting on behalf of an organization, you must have authority to submit information and make requests on its behalf. Any purchase or other binding commitment requires appropriate purchasing authority.',
    ],
  },
  {
    title: '3. Inquiries and Consultations',
    paragraphs: [
      'Submitting an inquiry, requesting a consultation or receiving preliminary information does not, by itself, create a purchase agreement or oblige either party to proceed with a transaction.',
      'Recommendations, preliminary assessments and proposals may depend on information you provide. You are responsible for supplying accurate and complete appliance, property, quantity and project details and notifying us of material changes.',
      'Consultations and supporting materials do not constitute legal advice, a building inspection, an engineering certification or an insurance coverage determination.',
    ],
  },
  {
    title: '4. Quotations and Orders',
    paragraphs: [
      'Unless expressly stated otherwise in a written quotation, website pricing and preliminary estimates are informational and do not constitute a binding offer.',
      'Commercial pricing, volume discounts, minimum quantities, payment terms, taxes, shipping charges, delivery arrangements and any installation services will be addressed in the applicable quotation or order documentation.',
      'Orders are subject to acceptance by RangeSafe. An automated acknowledgment of an inquiry or order request is not acceptance unless it expressly states otherwise.',
      'Cancellation, return, refund and warranty terms are those applicable to the accepted order and any rights provided by law. If these website Terms conflict with a separately agreed written commercial agreement, that agreement controls for the relevant transaction.',
    ],
  },
  {
    title: '5. Product Compatibility and Installation',
    paragraphs: [
      'Compatibility may vary by appliance manufacturer, model, configuration and condition. Product photographs, dimensions and general compatibility information should not be treated as confirmation that a product is suitable for every appliance.',
      'Follow the applicable RangeSafe product instructions and appliance manufacturer guidance when assessing compatibility, installing, testing and using a product. Where appropriate, seek assistance from a qualified professional.',
      'Do not force, modify or continue using a product that does not fit or function correctly. Contact RangeSafe if you have questions about compatibility or operation.',
    ],
  },
  {
    title: '6. Safety Information',
    paragraphs: [
      'RangeSafe products are designed to help reduce the risk of unintended stove actuation. They do not eliminate every risk associated with cooking appliances and should not be regarded as a guarantee against fire, gas release, injury or property damage.',
      'Safety products do not replace appropriate supervision, safe cooking practices, appliance maintenance, required alarms or other applicable safety measures.',
      'Property owners, managers and users remain responsible for their respective safety obligations. Nothing on this website guarantees that purchasing or installing a product will satisfy every applicable building, fire, housing, accessibility or other requirement, or qualify a property for insurance coverage or reduced premiums.',
      'Any stated certification, testing or compliance claim applies only to the specific product and scope identified in the relevant materials.',
    ],
  },
  {
    title: '7. Website Content and Availability',
    paragraphs: [
      'We aim to keep website information accurate and current, but information may contain errors or become outdated. We may update product descriptions, availability and other website content.',
      'Changes to the website do not automatically amend an accepted order, agreed price or existing contract.',
      'We do not guarantee uninterrupted or error-free website access. We may suspend or restrict access where reasonably necessary for maintenance, security or prevention of misuse.',
      'To the extent permitted by law, the website itself is provided on an “as available” basis. This provision does not disclaim or alter product warranties, binding product representations or rights that cannot lawfully be excluded.',
    ],
  },
  {
    title: '8. Intellectual Property',
    paragraphs: [
      'Website text, graphics, logos, photographs, videos, designs and other materials are owned by RangeSafe or used with permission and are protected by applicable intellectual property laws.',
      'You may view, download and share unmodified publicly available product materials internally within your organization to evaluate a potential purchase, provided you retain ownership notices and do not misrepresent their source.',
      'Other reproduction, modification, distribution or commercial exploitation requires permission from the relevant rights holder, except where permitted by law.',
      'Use of the website does not grant a license to RangeSafe patents, trademarks or other intellectual property beyond the limited permission stated above.',
    ],
  },
  {
    title: '9. Information You Submit',
    paragraphs: [
      'You retain ownership of materials you submit. You grant RangeSafe permission to use those materials as reasonably necessary to evaluate and respond to your inquiry, prepare a proposal or carry out an agreed project.',
      'You must have the right to provide submitted materials and authorize their use for those purposes. Please do not submit unnecessary personal information about residents, tenants or other individuals.',
      'If your inquiry requires the exchange of confidential business information, contact us to arrange appropriate confidentiality terms before submitting it.',
      'Personal information is handled as described in our Privacy Policy.',
    ],
  },
  {
    title: '10. Third-Party Websites',
    paragraphs: [
      "This website contains links to RangeSafe's retail customer website. Your use of that site is subject to its own terms and policies.",
    ],
  },
  {
    title: '11. Limitation of Liability',
    paragraphs: [
      'To the fullest extent permitted by applicable law, RangeSafe will not be liable for indirect, incidental, special or consequential losses arising solely from your use of, or inability to access, this website.',
      'This limitation concerns website use. It does not determine liability for products sold or services supplied, which is governed by the applicable agreement and law.',
      'Nothing in these Terms excludes or limits liability for fraud, willful misconduct, gross negligence, or any other liability that cannot lawfully be excluded or limited. Nothing limits mandatory statutory rights or applicable product warranty rights.',
    ],
  },
  {
    title: '12. Privacy',
    paragraphs: [
      'Please review our Privacy Policy for information about how we collect, use and disclose personal information in connection with the website and commercial inquiries.',
      'These Terms do not constitute consent to receive marketing communications or to the use of nonessential cookies where separate consent is required.',
    ],
  },
  {
    title: '13. Changes to These Terms',
    paragraphs: [
      'We may revise these Terms from time to time by posting an updated version and revising the “Last updated” date. Where required by law, we will provide additional notice or obtain agreement to material changes.',
      'Revised Terms apply prospectively and do not amend previously accepted orders or separate agreements without the required agreement of the parties.',
    ],
  },
  {
    title: '14. Governing Law',
    paragraphs: [
      'These Terms are governed by the laws of the Commonwealth of Virginia, United States, without regard to conflict-of-law principles, except where mandatory applicable law requires otherwise.',
      'Any separately agreed governing-law or dispute-resolution provisions applicable to a commercial transaction will govern that transaction.',
    ],
  },
  {
    title: '15. General Provisions',
    paragraphs: [
      'If a provision of these Terms is found unenforceable, the remaining provisions will continue in effect to the extent permitted by law.',
      'A failure to enforce a provision does not constitute a waiver of the right to enforce it later.',
      'These Terms govern website use and do not replace a separately agreed purchase agreement, confidentiality agreement or other written contract.',
    ],
  },
];

export default function TermsOfService() {
  usePageMeta(
    'Terms of Service | RangeSafe Commercial Solutions',
    'The Terms of Service that govern use of the RangeSafe commercial website, operated by RangeSafe, LLC.'
  );
  return (
    <main className="pt-16">
      <header className="mx-auto max-w-shell px-5 pb-16 pt-20 sm:px-8 lg:pt-28">
        <SectionHead
          as="h1"
          eyebrow="Legal"
          lines={['Terms of Service']}
          lead="These Terms of Service (“Terms”) govern your use of the RangeSafe commercial website, operated by RangeSafe, LLC (“RangeSafe,” “we,” “us” or “our”). Please read these Terms before using the website or submitting an inquiry. If you do not agree to these Terms, please discontinue use of the website."
        />
        <p className={`mt-6 ${LABEL}`} data-testid="terms-last-updated">
          Terms of Service · Last updated: October 26, 2026
        </p>
      </header>

      <div className="mx-auto max-w-shell space-y-12 px-5 pb-24 sm:px-8">
        {SECTIONS.map((s, i) => (
          <Reveal key={s.title} delay={Math.min(i * 0.03, 0.15)}>
            <section className="max-w-3xl" data-testid={`terms-section-${i + 1}`}>
              <h2 className={H2}>{s.title}</h2>
              {s.paragraphs.map((p) => (
                <p key={p.slice(0, 40)} className={`mt-4 ${BODY}`}>
                  {p}
                </p>
              ))}
              {s.bullets && (
                <ul className="mt-4 space-y-3">
                  {s.bullets.map((b) => (
                    <li key={b.slice(0, 40)} className="flex gap-4">
                      <span className="mt-2 h-1 w-1 shrink-0 bg-accent" aria-hidden="true" />
                      <span className={BODY}>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
              {s.after &&
                s.after.map((p) => (
                  <p key={p.slice(0, 40)} className={`mt-4 ${BODY}`}>
                    {p}
                  </p>
                ))}
            </section>
          </Reveal>
        ))}

        <Reveal>
          <section className="border border-line bg-panel/60 p-8 sm:p-10" data-testid="terms-contact">
            <p className={LABEL}>16. Contact Us</p>
            <p className={`mt-4 max-w-2xl ${BODY}`}>
              For questions about these Terms or a commercial inquiry, please contact:
            </p>
            <div className="mt-4 space-y-1 text-sm leading-relaxed text-dim">
              <p className="text-white">RangeSafe, LLC</p>
              <p>Email: privacy@rangesafe.com</p>
              <p>Mailing address: 4583 Highway 903, Bracey, Virginia 23919</p>
            </div>
          </section>
        </Reveal>
      </div>
    </main>
  );
}
